/**
 * Outbound telemetry queue.
 *
 * Buffers events and flushes on: batch size, interval, tab-hidden, and pagehide.
 * Every path is best-effort — a telemetry failure must never surface to the user,
 * so nothing here throws and nothing is retried aggressively.
 *
 * Transport is `fetch(..., { keepalive: true })`, NOT `navigator.sendBeacon`.
 * sendBeacon cannot set an Authorization header and this endpoint is
 * authenticated; keepalive survives page teardown *and* supports headers. Its
 * 64 KB limit is why TELEMETRY_MAX_BATCH exists.
 */

import { postActivityBatch } from "src/api/activityApi";
import {
  TELEMETRY_FLUSH_INTERVAL_MS,
  TELEMETRY_BUFFER_MAX,
  TELEMETRY_FLUSH_MAX_EVENTS,
  TELEMETRY_MAX_BATCH,
} from "src/constants/telemetry";

/** Activities the client is permitted to report. Must match the backend's
 *  CLIENT_REPORTABLE allowlist — anything else is rejected with 422. */
export type ClientActivityName =
  // Client-reported because the server's config route is also hit by the builder,
  // which would count an edit as a view. See backend route_events.py.
  | "dashboard.viewed"
  | "dashboard.dwell"
  // Editing a dashboard in the builder — a different activity from reading it, on
  // a different route (/builder/:code). The server cannot distinguish the two.
  | "builder.opened"
  | "builder.dwell"
  | "kpi.hovered"
  | "widget.exported"
  | "widget.menu_opened"
  | "filter.applied"
  | "filter.cleared"
  | "nav.page_viewed";

/**
 * The small JSON bag attached to an event.
 *
 * Deliberately narrow. The backend enforces the same allowlist, so anything added
 * here without a matching entry there is silently dropped server-side — the
 * browser is not trusted to decide what may be stored.
 */
export interface ActivityProperties {
  /** Which filters the user changed. KEY NAMES only — always safe to record. */
  filter_keys?: string[];
  /**
   * The values, for allowlisted keys only (see FILTER_VALUE_KEYS).
   * A filter not on that list contributes its name above and never its value,
   * because a filter value is the likeliest route for a patient identifier to
   * reach telemetry.
   */
  filter_values?: Record<string, string>;
}

export interface ActivityEventInput {
  name: ClientActivityName;
  dashboard_code?: string | null;
  widget_code?: string | null;
  subject_id?: string | null;
  duration_ms?: number;
  engaged_ms?: number;
  occurrences?: number;
  properties?: ActivityProperties;
  /**
   * When the activity actually happened, if that differs from "right now".
   *
   * Needed for anything COALESCED before it reaches `push()` — a KPI hover
   * finishes when the pointer leaves, but isn't pushed until the next drain
   * tick, by which point several other hovers may have finished too. Without
   * this, every one of them gets stamped with the drain's own instant instead
   * of its real one, and unrelated hovers end up sharing an identical time.
   * Omit it for anything pushed at the moment it happens — the default below
   * is already correct for those.
   */
  occurredAt?: string;
}

interface QueuedEvent extends ActivityEventInput {
  id: string;
  occurred_at: string;
}

const uuid = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

let buffer: QueuedEvent[] = [];
let intervalTimer: number | undefined;
/** Supplies the engaged-ms delta at flush time. Injected by the provider so this
 *  module stays unaware of how engagement is measured. */
let engagedDeltaSource: (() => number) | null = null;
/** Returns a drained engaged delta to the timer when a send did not happen. */
let engagedDeltaSink: ((ms: number) => void) | null = null;
/** Guards against the close beacon firing twice. visibilitychange and pagehide
 *  BOTH fire on a real tab close; event rows dedupe server-side by id, but the
 *  session's engaged_ms would otherwise accrue twice. */
let closeBeaconSent = false;
/**
 * When telemetry last had something real to say — read by the idle-only
 * heartbeat (TelemetryProvider) to decide whether it needs to fire.
 *
 * Previously the heartbeat tracked this itself, in its own closure, updated
 * ONLY by its own calls — so nothing about ordinary use (a click, a filter,
 * this module's own periodic auto-flush) ever reset it, and the heartbeat
 * fired on a fixed 5-minute clock for every user, active or not. Tracking it
 * here instead means ANY real flush counts as "recent contact," so the
 * heartbeat now only fires for someone genuinely idle.
 */
let lastActivityAt = 0;

export const telemetryQueue = {
  /** Buffer an event, flushing early if the batch is full. */
  push(event: ActivityEventInput): void {
    // `occurredAt` is a client-only override (see its doc comment) — pulled out
    // here so it never rides along into the request body as a redundant key
    // next to the `occurred_at` it produced.
    const { occurredAt, ...rest } = event;
    buffer.push({ ...rest, id: uuid(), occurred_at: occurredAt ?? new Date().toISOString() });
    if (buffer.length >= TELEMETRY_FLUSH_MAX_EVENTS) telemetryQueue.flush();
  },

  /**
   * Send buffered events plus the engaged-time delta. No-op when there is nothing new.
   *
   * `force` sends even with an empty payload. Needed on mount: a page reload leaves
   * the session marked `end_reason='closed'` and only a NON-closing report clears
   * it — but at that moment the buffer is empty and the engaged timer is at zero,
   * so an ordinary flush would short-circuit and the stale marker would survive
   * until the next tick.
   */
  flush(
    options: { closing?: boolean; programId?: number | null; force?: boolean } = {},
  ): void {
    const closing = options.closing ?? false;
    const force = options.force ?? false;

    // The double-fire guard: `visibilitychange` and `pagehide` BOTH fire on one
    // real close, and the second must not re-report engaged time.
    //
    // Note this does not drain the timer when it short-circuits — the engaged ms
    // stay in the client timer rather than being read and thrown away.
    const engagedDelta = closing && closeBeaconSent ? 0 : (engagedDeltaSource?.() ?? 0);

    const events = buffer.splice(0, TELEMETRY_MAX_BATCH);
    if (!events.length && !engagedDelta && !closing && !force) return;

    // Past the short-circuit above, so there is genuinely something to say —
    // this is "recent contact" for the idle-only heartbeat's purposes.
    lastActivityAt = Date.now();

    if (closing) {
      closeBeaconSent = true;
    } else {
      // Activity resumed, so any earlier close beacon is spent. Without this
      // reset the guard survived a TAB SWITCH — which also fires
      // `visibilitychange`→hidden — and the eventual real close then found the
      // guard already set and zeroed its engaged delta, silently under-counting
      // every session that involved switching away and back.
      closeBeaconSent = false;
    }

    // If the batch never reaches the server, put it back. `drain()` already ran
    // and `splice()` already removed the events, so without this a missing token
    // (common during a token refresh) or a network blip silently destroyed both
    // the engaged milliseconds and the events. Telemetry is allowed to be lossy
    // under pressure, but not to lose data on an ordinary refresh window.
    void postActivityBatch(
      {
        events,
        engaged_ms_delta: engagedDelta,
        closing,
        program_id: options.programId ?? null,
      },
      // keepalive only for the close beacon: it is the one request that must
      // survive page teardown.
      closing,
    ).then((sent) => {
      if (sent) return;
      if (engagedDelta) engagedDeltaSink?.(engagedDelta);
      if (events.length) {
        // Front of the queue: these are older than anything buffered since.
        buffer.unshift(...events);
        // Bounded, so a server outage cannot grow this without limit. Dropping the
        // OLDEST is deliberate — recent activity is the more useful half.
        if (buffer.length > TELEMETRY_BUFFER_MAX) {
          buffer = buffer.slice(buffer.length - TELEMETRY_BUFFER_MAX);
        }
      }
      // The beacon did not go out, so a later close must be allowed to try again.
      if (closing) closeBeaconSent = false;
    });

    // Anything beyond MAX_BATCH goes out on the next tick rather than being
    // dropped or oversizing the request.
    if (buffer.length) window.setTimeout(() => telemetryQueue.flush(), 0);
  },

  start(
    getEngagedDelta: () => number,
    creditEngagedDelta?: (ms: number) => void,
    // Called right before every periodic tick, before flush() reads the buffer.
    // Exists so completed KPI hovers (index.ts's drainKpiHovers) go out on this
    // module's own ~10s cadence like everything else, instead of only when the
    // heartbeat happens to fire — which, now that the heartbeat is genuinely
    // idle-only, could otherwise mean an ACTIVELY browsing user's KPI data
    // waits until they navigate away, since nothing else would ever drain it.
    beforePeriodicFlush?: () => void,
  ): void {
    engagedDeltaSource = getEngagedDelta;
    engagedDeltaSink = creditEngagedDelta ?? null;
    closeBeaconSent = false;
    window.clearInterval(intervalTimer);
    intervalTimer = window.setInterval(() => {
      beforePeriodicFlush?.();
      telemetryQueue.flush();
    }, TELEMETRY_FLUSH_INTERVAL_MS);
  },

  stop(): void {
    window.clearInterval(intervalTimer);
    intervalTimer = undefined;
    engagedDeltaSource = null;
    engagedDeltaSink = null;
    buffer = [];
  },

  /** Milliseconds since telemetry last had something real to send. */
  idleFor(): number {
    return lastActivityAt ? Date.now() - lastActivityAt : Infinity;
  },

  /** Re-arm after a fresh login, so the next tab close reports normally. */
  resetCloseGuard(): void {
    closeBeaconSent = false;
  },

  /** Test/debug visibility only. */
  pending: (): number => buffer.length,
};
